/**
 * Moneda como DATO, no como tipo cerrado — espejo de `lib/moneda.ts` del backend.
 *
 * Hasta el 01/09/2026 el CRM tenía la moneda como `'USD' | 'UYU'` en 171 lugares, el
 * símbolo resuelto a mano (`c === 'USD' ? 'U$S' : '$'`) en 22 y unos 15 formateadores
 * locales que asumían dos decimales. Cinco de las 18 monedas ofrecibles van en cero
 * (CLP, COP, HNL, NIO, PYG): "₲ 350.000.000,00" no existe.
 *
 * El símbolo y los decimales salen del catálogo que publica el backend
 * (`GET /signup/countries`, sembrado desde MercadoLibre) y se registran acá una vez al
 * arrancar (`MonedasCatalogoLoader`). Si una moneda no está, `Intl` conoce la tabla ISO.
 */
export type CurrencyCode = string;

/**
 * Las monedas del WALLET (comisiones, retiros, transferencias): USD y UYU por diseño — es
 * plata de Imperium con dos saldos. El backend corta con 400 si le llega otra
 * (`aMonedaDeWallet`); acá solo se tipa la frontera.
 */
export type MonedaDeWallet = 'USD' | 'UYU';

export interface MonedaInfo {
  id:       CurrencyCode;
  symbol:   string;
  decimals: number;
}

let catalogo = new Map<CurrencyCode, MonedaInfo>();

/** Lo llama el loader con el catálogo del backend. Idempotente. */
export function registrarMonedas(monedas: Array<{ currencyId: string; currencySymbol?: string; currencyDecimals: number }>): void {
  const nuevo = new Map<CurrencyCode, MonedaInfo>();
  for (const m of monedas) {
    const id = m.currencyId.toUpperCase();
    if (!nuevo.has(id)) nuevo.set(id, { id, symbol: m.currencySymbol || id, decimals: m.currencyDecimals });
  }
  // USD no tiene site propio pero es la segunda moneda de todos: 'U$S' es lo que el CRM mostró siempre.
  if (nuevo.size > 0 && !nuevo.has('USD')) nuevo.set('USD', { id: 'USD', symbol: 'U$S', decimals: 2 });
  if (nuevo.size > 0) catalogo = nuevo;
}

export function catalogoCargado(): boolean {
  return catalogo.size > 0;
}

function decimalesPorIntl(code: CurrencyCode): number | undefined {
  try {
    return new Intl.NumberFormat('es', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits;
  } catch {
    return undefined;
  }
}

export function decimalesDe(code: CurrencyCode | undefined | null): number {
  if (!code) return 2;
  return catalogo.get(code.toUpperCase())?.decimals ?? decimalesPorIntl(code) ?? 2;
}

/**
 * 'U$S' para USD, '$' para UYU, '₲' para PYG… Antes de que cargue el catálogo, las dos
 * de Uruguay salen igual que siempre; cualquier otra, su código.
 */
export function simboloDe(code: CurrencyCode | undefined | null): string {
  if (!code) return '';
  const id = code.toUpperCase();
  const info = catalogo.get(id);
  if (info) return info.symbol;
  if (id === 'USD') return 'U$S';
  if (id === 'UYU') return '$';
  return id;
}

/** Locale para miles y decimales. Uno solo hasta que el catálogo traiga el del país (F2 pendiente). */
export const LOCALE_NUMEROS = 'es-UY';

export interface FormatMoneyOpts {
  /** Cantidad fija de decimales, o `'moneda'` para los de la moneda. Por defecto 0 (precios redondos). */
  decimales?: number | 'moneda';
  /** `false` para el número solo. */
  conSimbolo?: boolean;
  /** 'U$S 1,2 M' para tarjetas y gráficos. */
  compacto?: boolean;
}

/**
 * "U$S 1.234" · "$ 25.000" · "₲ 350.000.000" · "U$S 1.234,50" con `decimales: 'moneda'`.
 * Vacío si el monto no es un número.
 */
export function formatMoney(amount: number | null | undefined, code: CurrencyCode | undefined | null, opts: FormatMoneyOpts = {}): string {
  if (amount == null || !Number.isFinite(amount)) return '';
  const cur = code ?? 'USD';
  const maxD = decimalesDe(cur);
  const d = opts.decimales === 'moneda' ? maxD : Math.min(opts.decimales ?? 0, maxD);
  const numero = opts.compacto
    ? new Intl.NumberFormat(LOCALE_NUMEROS, { notation: 'compact', maximumFractionDigits: 1 }).format(amount)
    : amount.toLocaleString(LOCALE_NUMEROS, { minimumFractionDigits: d, maximumFractionDigits: d });
  return opts.conSimbolo === false ? numero : `${simboloDe(cur)} ${numero}`;
}

/** Etiqueta para un selector: "U$S · Dólares (USD)" se arma en el hook; acá solo "USD (U$S)". */
export function etiquetaDeMoneda(m: MonedaInfo): string {
  return m.symbol && m.symbol !== m.id ? `${m.id} (${m.symbol})` : m.id;
}
